/**
 * Logging utilities for the application
 * 
 * This module provides a centralized set of logging functions that automatically
 * sanitize sensitive data before logging. Use these functions instead of direct
 * console.log calls to ensure sensitive data is not exposed in logs.
 */

import { safeLog, sanitizeLogObject, sanitizeLogString } from '../security/logSanitizer';

/**
 * Environment-aware logging configuration
 */
const isProduction = process.env.NODE_ENV === 'production';
const logLevel = isProduction ? 'error' : 'debug';

/**
 * Log levels
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

/**
 * Current log level based on environment
 */
const currentLogLevel = logLevel === 'error' ? LogLevel.ERROR : LogLevel.DEBUG;

/**
 * Log a debug message with sanitized values
 * Only logs in non-production environments
 *
 * @param message The message to log
 * @param args Optional additional arguments to include in the log
 */
export function logDebug(message: string, ...args: any[]): void {
  if (currentLogLevel <= LogLevel.DEBUG) {
    const sanitizedMessage = `[DEBUG] ${sanitizeLogString(message)}`;
    const sanitizedArgs = args.map(arg =>
      typeof arg === 'string' ? sanitizeLogString(arg) : sanitizeLogObject(arg)
    );
    
    safeLog.log(sanitizedMessage, ...sanitizedArgs);
  }
}

/**
 * Log an info message with sanitized values
 *
 * @param message The message to log
 * @param args Optional additional arguments to include in the log
 */
export function logInfo(message: string, ...args: any[]): void {
  if (currentLogLevel <= LogLevel.INFO) {
    const sanitizedMessage = `[INFO] ${sanitizeLogString(message)}`;
    const sanitizedArgs = args.map(arg =>
      typeof arg === 'string' ? sanitizeLogString(arg) : sanitizeLogObject(arg)
    );
    
    safeLog.info(sanitizedMessage, ...sanitizedArgs);
  }
}

/**
 * Log a warning message with sanitized values
 *
 * @param message The message to log
 * @param args Optional additional arguments to include in the log
 */
export function logWarning(message: string, ...args: any[]): void {
  if (currentLogLevel <= LogLevel.WARN) {
    const sanitizedMessage = `[WARN] ${sanitizeLogString(message)}`;
    const sanitizedArgs = args.map(arg =>
      typeof arg === 'string' ? sanitizeLogString(arg) : sanitizeLogObject(arg)
    );
    
    safeLog.warn(sanitizedMessage, ...sanitizedArgs);
  }
}

/**
 * Log an error message with sanitized values
 *
 * @param message The message to log
 * @param args Optional additional arguments to include in the log
 */
export function logError(message: string, ...args: any[]): void {
  if (currentLogLevel <= LogLevel.ERROR) {
    const sanitizedMessage = `[ERROR] ${sanitizeLogString(message)}`;
    const sanitizedArgs = args.map(arg =>
      typeof arg === 'string' ? sanitizeLogString(arg) : sanitizeLogObject(arg)
    );
    
    safeLog.error(sanitizedMessage, ...sanitizedArgs);
  }
}

/**
 * Default logger object with all methods
 */
export const logger = {
  debug: logDebug,
  info: logInfo,
  warn: logWarning,
  error: logError
};

/**
 * Legacy compatibility function to replace console.log
 * This makes it easier to migrate existing code
 * 
 * @param message The message to log
 * @param params Additional parameters to log
 */
export function log(message: string, ...params: any[]): void {
  if (currentLogLevel <= LogLevel.DEBUG) {
    const sanitizedMessage = sanitizeLogString(message);
    const sanitizedParams = params.map(param => 
      typeof param === 'string' ? sanitizeLogString(param) : sanitizeLogObject(param)
    );
    
    safeLog.log(sanitizedMessage, ...sanitizedParams);
  }
}